import React, { useEffect, useState } from 'react'
import { StyleSheet } from 'react-native'

import { Button, colors as zetColors } from '@zet/design-system'
import { Stack } from '@zet/design-system/layout'
import { Text } from '@zet/design-system/typography'
import { i18n } from '@zet/localization'
import { zetApi } from '@zet/network'
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from 'react-native-confirmation-code-field'
import RNOtpVerify from 'react-native-otp-verify'

import ResetOtp from './resetOtp'
import VerifyOtpHeading from './verifyOtpHeading'

const OTP_COUNT = 4
const TIMER_COUNT = 30

type VerifyOtpProps = {
  mobileNumber?: string
  onReset: (phoneNumber: string) => void
  onClickSubmit: (otp: string, phoneNumber?: string) => void
  isVerifying?: boolean
  heading?: string
  withHeading: boolean
  withResend: boolean
  FooterComponent?: React.ElementType
  otpCount?: number
  timerCount?: number
}
const VerifyOtp = ({
  mobileNumber,
  onReset,
  onClickSubmit,
  isVerifying,
  heading,
  withResend = true,
  withHeading = true,
  FooterComponent,
  otpCount = OTP_COUNT,
  timerCount = TIMER_COUNT,
}: VerifyOtpProps) => {
  const [otp, setOtp] = useState<string>('')
  const { locale = 'en' } = zetApi.defaults.headers.common

  const [props, getCellOnLayoutHandler] = useClearByFocusCell({
    value: otp,
    setValue: setOtp,
  })

  useEffect(() => {
    RNOtpVerify.getOtp()
      .then((p) => RNOtpVerify.addListener(otpHandler))
      .catch((p) => console.log(p))

    return () => RNOtpVerify.removeListener()
  }, [])

  const otpHandler = (message: string) => {
    if (message) {
      const otp: string | undefined = /(\d{4})/g.exec(message)?.[1]
      if (otp) {
        setOtp(otp)
        onClickSubmit(otp, mobileNumber)
      }
    }
  }

  const ref = useBlurOnFulfill({ value: otp, cellCount: otpCount })

  const onOTPSubmit = () => onClickSubmit(otp, mobileNumber)

  return (
    <Stack className="grow">
      {withHeading && (
        <VerifyOtpHeading heading={heading} mobileNumber={mobileNumber} />
      )}
      <CodeField
        ref={ref}
        value={otp}
        {...props}
        onChangeText={(e) => {
          setOtp(e)
        }}
        rootStyle={styles.codeFieldRoot}
        caretHidden={true}
        cellCount={otpCount}
        keyboardType="number-pad"
        renderCell={({ index, symbol, isFocused }) => (
          <Text
            key={index}
            onLayout={getCellOnLayoutHandler(index)}
            style={[styles.cell, isFocused && styles.focusCell]}
          >
            {symbol || (isFocused ? <Cursor /> : null)}
          </Text>
        )}
      />
      {withResend && (
        <ResetOtp
          timerCount={timerCount}
          locale={locale}
          handleSubmit={() => {
            onReset(mobileNumber)
            setOtp('')
          }}
        />
      )}
      {FooterComponent ? (
        <FooterComponent
          isValid={otp.length === otpCount}
          onSubmit={onOTPSubmit}
        />
      ) : (
        <Stack className="border-grays-40 mt-[60] w-full border-t p-3">
          <Button
            fullWidth
            onPress={onOTPSubmit}
            size={'lg'}
            loading={isVerifying}
          >
            {i18n.t('verifyPhoneNumber')}
          </Button>
        </Stack>
      )}
    </Stack>
  )
}

export default VerifyOtp

const styles = StyleSheet.create({
  codeFieldRoot: {
    marginTop: 32,
    marginHorizontal: 32,
    justifyContent: 'space-around',
  },
  cell: {
    width: 48,
    height: 48,
    lineHeight: 47,
    fontSize: 16,
    fontWeight: '500',
    borderWidth: 1,
    borderColor: zetColors.grays[40],
    textAlign: 'center',
    borderRadius: 6,
    backgroundColor: '#F9F9FB',
    paddingHorizontal: 0,
    marginBottom: 20,
  },
  focusCell: {
    borderColor: zetColors.primary[50],
  },
})
